export type PeriodKey = "this_month" | "previous_month" | "this_quarter" | "custom";

export interface PeriodRange {
  key: PeriodKey;
  /** Inclusive, YYYY-MM-DD */
  from: string;
  /** Inclusive, YYYY-MM-DD */
  to: string;
  label: string;
}

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

function startOfQuarter(d: Date): Date {
  const q = Math.floor(d.getMonth() / 3);
  return new Date(d.getFullYear(), q * 3, 1);
}

function endOfQuarter(d: Date): Date {
  const q = Math.floor(d.getMonth() / 3);
  return new Date(d.getFullYear(), q * 3 + 3, 0);
}

const MONTH_LABEL = new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" });

export const PERIOD_PRESETS: Array<{ key: Exclude<PeriodKey, "custom">; label: string }> = [
  { key: "this_month", label: "This Month" },
  { key: "previous_month", label: "Previous Month" },
  { key: "this_quarter", label: "This Quarter" },
];

export function getPeriodRange(key: PeriodKey, custom?: { from: string; to: string }): PeriodRange {
  const now = new Date();

  if (key === "previous_month") {
    const target = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const from = startOfMonth(target);
    const to = endOfMonth(target);
    return { key, from: toIsoDate(from), to: toIsoDate(to), label: MONTH_LABEL.format(from) };
  }

  if (key === "this_quarter") {
    const from = startOfQuarter(now);
    const to = endOfQuarter(now);
    const quarter = Math.floor(now.getMonth() / 3) + 1;
    return { key, from: toIsoDate(from), to: toIsoDate(to), label: `Q${quarter} ${now.getFullYear()}` };
  }

  if (key === "custom") {
    const from = custom?.from || toIsoDate(startOfMonth(now));
    const to = custom?.to || toIsoDate(endOfMonth(now));
    return { key, from, to, label: `${from} – ${to}` };
  }

  const from = startOfMonth(now);
  const to = endOfMonth(now);
  return { key: "this_month", from: toIsoDate(from), to: toIsoDate(to), label: MONTH_LABEL.format(from) };
}
